import type { ComponentProps } from 'react'
import type { OrderDirection } from '@/helpers/types'
import { cn } from '@/helpers/utils'
import {
	ChevronDownIcon,
	ChevronSelectorVerticalIcon,
	ChevronUpIcon
} from './icons'
import { Skeleton } from './skeleton'

const getPinningStyle = (
	pinningPosition: 'left' | 'right' | false,
	pinningStart: number
): { left?: number; right?: number } | undefined => {
	if (pinningPosition === 'left') {
		return { left: pinningStart }
	}
	if (pinningPosition === 'right') {
		return { right: pinningStart }
	}
	return undefined
}

const getSortIcon = (orderDirection: `${OrderDirection}` | null) => {
	if (orderDirection === 'asc') {
		return <ChevronUpIcon />
	}
	if (orderDirection === 'desc') {
		return <ChevronDownIcon />
	}
	return <ChevronSelectorVerticalIcon />
}

export const tableBackgroundClassName = 'bg-neutral-white' as const
export const tableRowBackgroundHoverClassName = 'hover:bg-neutral-50' as const
export const tableRowBackgroundHoverGroupClassName =
	'group-hover/row:bg-neutral-50' as const
export const tableRowBackgroundSelectedGroupClassName =
	'group-data-[selected]/row:bg-neutral-100' as const

function Root({ className, ...props }: ComponentProps<'table'>) {
	return (
		<table
			className={cn(
				'isolate w-full table-fixed border-spacing-0',
				tableBackgroundClassName,
				className
			)}
			{...props}
		/>
	)
}

function Header({ className, ...props }: ComponentProps<'thead'>) {
	return (
		<thead className={cn('relative isolate z-2', className)} {...props} />
	)
}

function HeaderRow({ className, ...props }: ComponentProps<'tr'>) {
	return <tr className={cn('relative', className)} {...props} />
}

function HeaderGapRow({
	columnsLength,
	...props
}: ComponentProps<'tr'> & { columnsLength: number }) {
	return (
		<tr
			className={cn('h-1 w-full', tableBackgroundClassName)}
			{...props}
			data-slot='table-header-gap-row'
		>
			<th colSpan={columnsLength} />
		</tr>
	)
}

function HeaderCell({
	className,
	children,
	pinningPosition = false,
	pinningStart = 0,
	labelAlign = 'start',
	style,
	...props
}: ComponentProps<'th'> & {
	pinningPosition?: 'left' | 'right' | false
	pinningStart?: number
	/** Where the label sits in the cell. `end` for a right-anchored column. */
	labelAlign?: 'start' | 'end'
}) {
	return (
		<th
			className={cn(
				'group/header-cell h-11 px-6 first:rounded-l-md last:rounded-r-md',
				'bg-neutral-white text-left font-medium text-neutral-500 text-xs',
				pinningPosition ? 'sticky z-1' : 'relative',
				className
			)}
			style={{
				...getPinningStyle(pinningPosition, pinningStart),
				...style
			}}
			{...props}
		>
			<div
				className={cn(
					'flex items-center gap-2',
					labelAlign === 'end' && 'justify-end'
				)}
			>
				{children}
			</div>
		</th>
	)
}

function HeaderCellLabel({ className, ...props }: ComponentProps<'span'>) {
	return (
		<span
			className={cn('select-none truncate uppercase', className)}
			{...props}
		/>
	)
}

function Resizer(props: ComponentProps<'div'>) {
	return (
		<div
			className={cn(
				'touch-action-none user-select-none absolute top-0 right-0 h-full w-1 cursor-col-resize transition duration-200 ease-in-out',
				'invisible group-hover/header-cell:visible',
				'bg-neutral-400 hover:bg-neutral-500'
			)}
			{...props}
		/>
	)
}

function SortButton({
	orderDirection,
	type = 'button',
	className,
	...props
}: ComponentProps<'button'> & {
	orderDirection: `${OrderDirection}` | null
}) {
	return (
		<button
			className={cn(
				'size-4 shrink-0 cursor-pointer [&>svg]:size-full',
				!orderDirection && 'invisible group-hover/header-cell:visible',
				className
			)}
			type={type}
			{...props}
		>
			{getSortIcon(orderDirection)}
		</button>
	)
}

function Body({ className, ...props }: ComponentProps<'tbody'>) {
	return <tbody className={cn('isolate', className)} {...props} />
}

function Row({ className, ...props }: ComponentProps<'tr'>) {
	return (
		<tr
			className={cn(
				'group/row relative border-neutral-200 border-b transition duration-200 ease-in-out hover:bg-neutral-50',
				className
			)}
			{...props}
		/>
	)
}

function Cell({
	className,
	pinningPosition = false,
	pinningStart = 0,
	style,
	...props
}: ComponentProps<'td'> & {
	pinningPosition?: 'left' | 'right' | false
	pinningStart?: number
}) {
	return (
		<td
			className={cn(
				'h-18 truncate px-6 text-left text-neutral-700 text-sm',
				tableBackgroundClassName,
				tableRowBackgroundHoverGroupClassName,
				tableRowBackgroundSelectedGroupClassName,
				'last:group-data-[selected]/row:rounded-r-md first:group-data-[selected]/row:rounded-l-md',
				pinningPosition ? 'sticky z-1' : 'relative',
				className
			)}
			style={{
				...getPinningStyle(pinningPosition, pinningStart),
				...style
			}}
			{...props}
		/>
	)
}

function CellSkeleton({
	className,
	...props
}: ComponentProps<typeof Skeleton>) {
	return <Skeleton className={cn('h-10 w-full', className)} {...props} />
}

export const Table = {
	Root,
	Header,
	HeaderRow,
	HeaderGapRow,
	HeaderCell,
	HeaderCellLabel,
	SortButton,
	Resizer,
	Body,
	Row,
	Cell,
	CellSkeleton
}
